import { ArrowRight, LoaderCircle, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useOrgWorkflowsQuery } from '@/features/workflows/hooks/useWorkflowsQuery'
import { CobaltEntityRow } from './CobaltEntityRow'
import type {
  CobaltSyncConfig,
  CobaltSyncConfigUpdate,
  CobaltSyncEntity,
  CobaltSyncEntityState,
  ReportDownloadStatusResponse,
} from './types'
import {
  useReportDownloadStatusQuery,
  useRunReportDownloadMutation,
} from './useReportDownloads'

const COBALT_ENTITIES: ReadonlyArray<{
  entity: CobaltSyncEntity
  label: string
}> = [
  { entity: 'facilities', label: 'Facilities' },
  { entity: 'providers', label: 'Providers' },
  { entity: 'patients', label: 'Patients' },
  { entity: 'appointments', label: 'Appointments' },
  { entity: 'referrals', label: 'Referrals' },
  { entity: 'orders', label: 'Orders' },
  { entity: 'availability', label: 'Availability' },
]

const VISITS_WORKFLOW_SLUG = 'download-visit'

interface EntitySyncControlsProps {
  cobaltConnected: boolean
  canManageCobalt: boolean
  cobaltConfigs?: CobaltSyncConfig[]
  cobaltEntities?: CobaltSyncEntityState[]
  isCobaltLoading: boolean
  isCobaltError: boolean
  isCobaltConfigLoading: boolean
  isCobaltConfigError: boolean
  startingCobaltEntity?: CobaltSyncEntity
  cancellingCobaltEntity?: CobaltSyncEntity
  updatingCobaltConfigEntity?: CobaltSyncEntity
  computerUseEnabled: boolean
  onStartCobalt: (entity: CobaltSyncEntity) => void
  onCancelCobalt: (entity: CobaltSyncEntity) => void
  onUpdateCobaltConfig: (
    entity: CobaltSyncEntity,
    changes: CobaltSyncConfigUpdate
  ) => Promise<boolean>
}

function reportStatusLabel(
  status: ReportDownloadStatusResponse | undefined,
  isLoading: boolean,
  isError: boolean
): string {
  if (isLoading) return 'Checking'
  if (isError) return 'Unavailable'
  if (status?.state === 'pending' || status?.state === 'running') {
    return 'Running'
  }
  if (status?.state === 'completed') return 'Completed'
  if (status?.state === 'failed') return 'Failed'
  return 'Never synced'
}

function VisitsAndEncountersRow({
  status,
  isLoading,
  isError,
  isStarting,
  onStart,
}: {
  status?: ReportDownloadStatusResponse
  isLoading: boolean
  isError: boolean
  isStarting: boolean
  onStart: () => void
}) {
  const working =
    isStarting || status?.state === 'pending' || status?.state === 'running'
  const canRun =
    !working &&
    !isLoading &&
    !isError &&
    (status?.state === 'not_started' ||
      status?.state === 'completed' ||
      status?.state === 'failed')

  return (
    <div role="group" aria-label="Visits & Encounters" className="p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-primary-high-contrast">
            Visits &amp; Encounters
          </p>
          <p className="text-xs text-muted-foreground">
            {reportStatusLabel(status, isLoading, isError)}
          </p>
          {status?.session_id && (
            <Link
              to={`/sessions/${status.session_id}`}
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-solid hover:underline">
              View session
              <ArrowRight className="size-3" />
            </Link>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!canRun}
          onClick={onStart}>
          {working ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
          Sync
        </Button>
      </div>
    </div>
  )
}

export function EntitySyncControls({
  cobaltConnected,
  canManageCobalt,
  cobaltConfigs,
  cobaltEntities,
  isCobaltLoading,
  isCobaltError,
  isCobaltConfigLoading,
  isCobaltConfigError,
  startingCobaltEntity,
  cancellingCobaltEntity,
  updatingCobaltConfigEntity,
  computerUseEnabled,
  onStartCobalt,
  onCancelCobalt,
  onUpdateCobaltConfig,
}: EntitySyncControlsProps) {
  const { data: orgWorkflows } = useOrgWorkflowsQuery()
  const visitsAvailable =
    computerUseEnabled &&
    !!orgWorkflows?.some(
      (workflow) =>
        workflow.slug === VISITS_WORKFLOW_SLUG && !workflow.deprecated
    )
  const visitsStatus = useReportDownloadStatusQuery('visits', visitsAvailable)
  const runVisits = useRunReportDownloadMutation('visits')

  // Reports the outcome the way every neighbouring action does; a bare
  // mutate() left a failed export completely silent.
  const startVisitsRun = async () => {
    try {
      await runVisits.mutateAsync()
      toast.success('Visits & Encounters export started.')
    } catch {
      toast.error('Visits & Encounters export could not be started. Try again.')
    }
  }

  if (!cobaltConnected && !visitsAvailable) return null

  const statesByEntity = new Map(
    cobaltEntities?.map((state) => [state.entity, state])
  )
  const configsByEntity = new Map(
    cobaltConfigs?.map((config) => [config.entity, config])
  )

  return (
    <section
      aria-labelledby="connection-entities-heading"
      className="rounded-xl border border-border bg-card p-4">
      <h3
        id="connection-entities-heading"
        className="font-medium text-primary-high-contrast">
        Entities
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Data synchronized from eClinicalWorks.
      </p>

      <div className="mt-4 divide-y divide-border rounded-lg border border-border">
        {cobaltConnected &&
          COBALT_ENTITIES.map(({ entity, label }) => (
            <CobaltEntityRow
              key={entity}
              entity={entity}
              label={label}
              config={configsByEntity.get(entity)}
              state={statesByEntity.get(entity)}
              isLoading={isCobaltLoading}
              isError={isCobaltError}
              isConfigLoading={isCobaltConfigLoading}
              isConfigError={isCobaltConfigError}
              canManage={canManageCobalt}
              isStarting={startingCobaltEntity === entity}
              isCancelling={cancellingCobaltEntity === entity}
              isUpdatingConfig={updatingCobaltConfigEntity === entity}
              onStart={onStartCobalt}
              onCancel={onCancelCobalt}
              onUpdateConfig={onUpdateCobaltConfig}
            />
          ))}
        {visitsAvailable && (
          <VisitsAndEncountersRow
            status={visitsStatus.data}
            isLoading={visitsStatus.isLoading}
            isError={visitsStatus.isError}
            isStarting={runVisits.isPending}
            onStart={() => void startVisitsRun()}
          />
        )}
      </div>
    </section>
  )
}
